/**
 * Version Comparison Panel
 * Provides side-by-side version comparison with diff highlighting
 */

import React, { useState, useEffect } from 'react';
import { DocumentVersion } from '../../../shared/document/types';
import { DocumentService } from '../../../shared/document/DocumentService';
import { 
  ArrowLeftIcon, 
  ArrowRightIcon, 
  DocumentTextIcon,
  ClockIcon,
  UserIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

interface VersionComparisonPanelProps {
  documentId: string;
  leftVersion?: DocumentVersion;
  rightVersion?: DocumentVersion;
  onVersionSelect?: (version: DocumentVersion, side: 'left' | 'right') => void;
  onClose?: () => void;
}

interface DiffResult {
  type: 'added' | 'removed' | 'unchanged';
  content: string;
  lineNumber?: number;
}

export function VersionComparisonPanel({
  documentId,
  leftVersion,
  rightVersion,
  onVersionSelect,
  onClose,
}: VersionComparisonPanelProps): React.JSX.Element {
  const [leftContent, setLeftContent] = useState<string>('');
  const [rightContent, setRightContent] = useState<string>('');
  const [diffResults, setDiffResults] = useState<DiffResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [selectedLeftVersion, setSelectedLeftVersion] = useState<DocumentVersion | undefined>(leftVersion);
  const [selectedRightVersion, setSelectedRightVersion] = useState<DocumentVersion | undefined>(rightVersion);

  // Load versions on mount
  useEffect(() => {
    loadVersions();
  }, [documentId]);

  // Load content when versions change
  useEffect(() => {
    if (selectedLeftVersion && selectedRightVersion) {
      loadVersionContent();
    }
  }, [selectedLeftVersion, selectedRightVersion]);

  // Calculate diff when content changes
  useEffect(() => {
    if (leftContent && rightContent) {
      calculateDiff();
    }
  }, [leftContent, rightContent]);

  const loadVersions = async () => {
    try {
      const versionList = await DocumentService.getDocumentVersions(documentId);
      setVersions(versionList.sort((a, b) => b.version - a.version));
    } catch (error) {
      console.error('Failed to load versions:', error);
    }
  };

  const loadVersionContent = async () => {
    if (!selectedLeftVersion || !selectedRightVersion) return;

    setIsLoading(true);
    try {
      const [leftContentData, rightContentData] = await Promise.all([
        DocumentService.getLexicalContent(selectedLeftVersion.document_id),
        DocumentService.getLexicalContent(selectedRightVersion.document_id),
      ]);

      setLeftContent(leftContentData);
      setRightContent(rightContentData);
    } catch (error) {
      console.error('Failed to load version content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateDiff = () => {
    // Simple line-by-line diff implementation
    const leftLines = leftContent.split('\n');
    const rightLines = rightContent.split('\n');
    const diff: DiffResult[] = [];

    const maxLines = Math.max(leftLines.length, rightLines.length);

    for (let i = 0; i < maxLines; i++) {
      const leftLine = leftLines[i] || '';
      const rightLine = rightLines[i] || '';

      if (leftLine === rightLine) {
        diff.push({
          type: 'unchanged',
          content: leftLine,
          lineNumber: i + 1,
        });
      } else {
        if (leftLine) {
          diff.push({
            type: 'removed',
            content: leftLine,
            lineNumber: i + 1,
          });
        }
        if (rightLine) {
          diff.push({
            type: 'added',
            content: rightLine,
            lineNumber: i + 1,
          });
        }
      }
    }

    setDiffResults(diff);
  };

  const handleVersionSelect = (version: DocumentVersion, side: 'left' | 'right') => {
    if (side === 'left') {
      setSelectedLeftVersion(version);
    } else {
      setSelectedRightVersion(version);
    }
    onVersionSelect?.(version, side);
  };

  const getDiffStats = () => {
    const added = diffResults.filter(d => d.type === 'added').length;
    const removed = diffResults.filter(d => d.type === 'removed').length;
    const unchanged = diffResults.filter(d => d.type === 'unchanged').length;
    
    return { added, removed, unchanged };
  };

  const diffStats = getDiffStats();

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-3">
          <DocumentTextIcon className="h-6 w-6 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Version Comparison
          </h2>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Diff Stats */}
          <div className="flex items-center space-x-3 text-sm">
            <span className="flex items-center space-x-1 text-green-600">
              <CheckCircleIcon className="h-4 w-4" />
              <span>+{diffStats.added}</span>
            </span>
            <span className="flex items-center space-x-1 text-red-600">
              <ExclamationTriangleIcon className="h-4 w-4" />
              <span>-{diffStats.removed}</span>
            </span>
            <span className="text-gray-500">
              {diffStats.unchanged} unchanged
            </span>
          </div>
          
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Version Selectors */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        {/* Left Version Selector */}
        <div className="flex items-center space-x-3">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Left:</span>
          <select
            value={selectedLeftVersion?.version || ''}
            onChange={(e) => {
              const version = versions.find(v => v.version === parseInt(e.target.value));
              if (version) handleVersionSelect(version, 'left');
            }}
            className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">Select version</option>
            {versions.map((version) => (
              <option key={version.id} value={version.version}>
                v{version.version} - {new Date(version.created_at).toLocaleDateString()}
              </option>
            ))}
          </select>
        </div>

        {/* Compare Icon */}
        <div className="flex items-center space-x-2">
          <ArrowLeftIcon className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-500">compare</span>
          <ArrowRightIcon className="h-4 w-4 text-gray-400" />
        </div>

        {/* Right Version Selector */}
        <div className="flex items-center space-x-3">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Right:</span>
          <select
            value={selectedRightVersion?.version || ''}
            onChange={(e) => {
              const version = versions.find(v => v.version === parseInt(e.target.value));
              if (version) handleVersionSelect(version, 'right');
            }}
            className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">Select version</option>
            {versions.map((version) => (
              <option key={version.id} value={version.version}>
                v{version.version} - {new Date(version.created_at).toLocaleDateString()}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Comparison Content */}
      <div className="flex-1 flex overflow-hidden">
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            <span className="ml-3 text-gray-600 dark:text-gray-400">Loading versions...</span>
          </div>
        ) : selectedLeftVersion && selectedRightVersion ? (
          <div className="flex-1 flex">
            {/* Left Version */}
            <div className="flex-1 border-r border-gray-200 dark:border-gray-700">
              <div className="p-4 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <h3 className="font-medium text-gray-900 dark:text-gray-100">
                  Version {selectedLeftVersion.version}
                </h3>
                <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400 mt-1">
                  <span className="flex items-center space-x-1">
                    <ClockIcon className="h-4 w-4" />
                    <span>{new Date(selectedLeftVersion.created_at).toLocaleString()}</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <UserIcon className="h-4 w-4" />
                    <span>{selectedLeftVersion.created_by}</span>
                  </span>
                </div>
              </div>
              <div className="p-4 overflow-auto h-full">
                <pre className="text-sm font-mono text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                  {leftContent}
                </pre>
              </div>
            </div>

            {/* Right Version */}
            <div className="flex-1">
              <div className="p-4 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <h3 className="font-medium text-gray-900 dark:text-gray-100">
                  Version {selectedRightVersion.version}
                </h3>
                <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400 mt-1">
                  <span className="flex items-center space-x-1">
                    <ClockIcon className="h-4 w-4" />
                    <span>{new Date(selectedRightVersion.created_at).toLocaleString()}</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <UserIcon className="h-4 w-4" />
                    <span>{selectedRightVersion.created_by}</span>
                  </span>
                </div>
              </div>
              <div className="p-4 overflow-auto h-full">
                <pre className="text-sm font-mono text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                  {rightContent}
                </pre>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                Select Versions to Compare
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                Choose two versions from the dropdowns above to start comparing.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Diff Navigation */}
      {diffResults.length > 0 && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <button className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              <span className="text-sm text-gray-500">Previous change</span>
            </div>
            
            <div className="text-sm text-gray-500">
              Showing all changes
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Next change</span>
              <button className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VersionComparisonPanel; 